import { useMemo } from "react";
import { Box, Divider, Tooltip, Typography, useTheme } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { SectionCard } from "@/components/DesignSystem";
import NotMeasured from "./NotMeasured.tsx";
import { riskColors } from "@/theme/eventsTokens";
import type { TrafficOrigin, TrafficOriginGroup, TrafficOriginTotals } from "@/types";
import { formatSnapshotFreshness } from "@/utils/snapshotFreshness.ts";

type SortField = "attempt_count" | "ip_count";

interface TrafficOriginsPanelProps {
  readonly title: string;
  readonly metricLabel: string;
  /** null when the query behind this side failed — see NotMeasured. */
  readonly group: TrafficOriginGroup | null;
  readonly variant?: "legitimate" | "hostile";
  readonly sortBy: SortField;
  readonly snapshotLastUpdated?: string;
}

export default function TrafficOriginsPanel({
  title,
  metricLabel,
  group,
  variant = "legitimate",
  sortBy,
  snapshotLastUpdated,
}: TrafficOriginsPanelProps) {
  // Each ranking arrives already ordered by the metric it ranks on, so the
  // toggle swaps lists rather than re-sorting one.
  const rows = useMemo(() => {
    if (!group) return [];
    return sortBy === "ip_count" ? group.by_ip_count : group.by_attempts;
  }, [group, sortBy]);

  const maxValue = useMemo(
    () => Math.max(1, ...rows.map((e) => e[sortBy])),
    [rows, sortBy],
  );

  const accent = variant === "hostile" ? riskColors.high.main : riskColors.low.main;
  const barBg = variant === "hostile" ? riskColors.high.muted : riskColors.low.muted;

  if (!group) {
    return (
      <SectionCard title={title} sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <NotMeasured source="this traffic-origin data" />
      </SectionCard>
    );
  }

  return (
    <SectionCard title={title} sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <FreshnessAndInfo snapshotLastUpdated={snapshotLastUpdated} />
      <TotalsSummary
        sortBy={sortBy}
        metricLabel={metricLabel}
        totals={group.totals}
        shown={rows.length}
        accent={accent}
      />
      <Divider sx={{ mb: 1.5 }} />
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        {rows.map((entry, i) => (
          <OriginRow
            key={`${entry.asn}|${entry.name}`}
            entry={entry}
            rank={i + 1}
            ratio={entry[sortBy] / maxValue}
            accent={accent}
            barBg={barBg}
            sortBy={sortBy}
            metricLabel={metricLabel}
          />
        ))}
      </Box>
    </SectionCard>
  );
}

function TotalsSummary({
  sortBy,
  metricLabel,
  totals,
  shown,
  accent,
}: {
  readonly sortBy: SortField;
  readonly metricLabel: string;
  readonly totals: TrafficOriginTotals;
  readonly shown: number;
  readonly accent: string;
}) {
  const primaryLabel = sortBy === "attempt_count" ? metricLabel : "IPs";
  const secondary =
    sortBy === "attempt_count"
      ? `${totals.ip_count.toLocaleString()} IPs`
      : `${totals.attempt_count.toLocaleString()} ${metricLabel.toLowerCase()}`;
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", mb: 1 }}>
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        {secondary}
        {totals.asn_count > shown && ` · top ${shown} of ${totals.asn_count.toLocaleString()} ASNs`}
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
        <Typography
          variant="caption"
          sx={{ mb: 0.5, fontWeight: 600, fontSize: "0.75rem", color: accent, textTransform: "capitalize" }}
        >
          {primaryLabel}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: "1.25rem", color: accent }}>
          {(sortBy === "attempt_count" ? totals.attempt_count : totals.ip_count).toLocaleString()}
        </Typography>
      </Box>
    </Box>
  );
}

function OriginRow({
  entry,
  rank,
  ratio,
  accent,
  barBg,
  sortBy,
  metricLabel,
}: {
  readonly entry: TrafficOrigin;
  readonly rank: number;
  readonly ratio: number;
  readonly accent: string;
  readonly barBg: string;
  readonly sortBy: SortField;
  readonly metricLabel: string;
}) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        py: 1.25,
        px: 1,
        borderRadius: 1,
        "&:hover": { bgcolor: barBg },
        "&:not(:last-child)": { borderBottom: `1px solid ${theme.palette.divider}` },
      }}
    >
      <Typography
        variant="caption"
        sx={{ width: 22, flexShrink: 0, fontWeight: 700, color: "text.disabled", textAlign: "right" }}
      >
        {rank}
      </Typography>
      <OriginRowContent entry={entry} ratio={ratio} accent={accent} barBg={barBg} sortBy={sortBy} metricLabel={metricLabel} />
    </Box>
  );
}

function OriginRowContent({
  entry, ratio, accent, barBg, sortBy, metricLabel,
}: {
  readonly entry: TrafficOrigin;
  readonly ratio: number;
  readonly accent: string;
  readonly barBg: string;
  readonly sortBy: SortField;
  readonly metricLabel: string;
}) {
  const altMetric = sortBy === "ip_count"
    ? `${entry.attempt_count.toLocaleString()} ${metricLabel}`
    : `${entry.ip_count.toLocaleString()} IPs`;

  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 0.25 }}>
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
        >
          {entry.asn} ({entry.name})
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 700, flexShrink: 0, ml: 1, color: accent }}>
          {entry[sortBy].toLocaleString()}
        </Typography>
      </Box>
      <Box sx={{ height: 4, borderRadius: 2, bgcolor: barBg, overflow: "hidden", mb: 0.5 }}>
        <Box
          sx={{
            width: `${ratio * 100}%`,
            height: "100%",
            borderRadius: 2,
            bgcolor: accent,
            opacity: 0.7,
            transition: "width 0.3s ease",
          }}
        />
      </Box>
      <Typography variant="caption" color="text.secondary">
        {entry.country_code ? `${entry.country_code} · ` : ""}{altMetric}
      </Typography>
    </Box>
  );
}

function FreshnessAndInfo({ snapshotLastUpdated }: { readonly snapshotLastUpdated?: string }) {
  const formatted = formatSnapshotFreshness(snapshotLastUpdated);
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: -1.5, mb: 1.5 }}>
      <Typography variant="caption" sx={{ color: "text.secondary", fontStyle: "italic" }}>
        {formatted ? `ASN metadata as of ${formatted}` : "ASN metadata from the latest snapshot"}
      </Typography>
      <Tooltip
        arrow
        title="ASN and country information is added from a periodic threat-intelligence snapshot. Totals may trail the live telemetry cards above while recent activity is still being attributed."
      >
        <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.secondary", cursor: "help" }} />
      </Tooltip>
    </Box>
  );
}

